import { motion } from "framer-motion";
import { AdaptiveText, type AdaptiveTextProps } from "./Content";
import { Label } from "./ui/label";

interface TimelineItem {
	name: string;
	date: string;
	dateTime: string;
	description: AdaptiveTextProps["portableText"];
}

// Animation variants
const containerVariants = {
	hidden: { opacity: 0 },
	visible: {
		opacity: 1,
		transition: {
			staggerChildren: 0.15,
			delayChildren: 0.1,
		},
	},
};

const itemVariants = {
	hidden: { opacity: 0, y: 20 },
	visible: {
		opacity: 1,
		y: 0,
		transition: {
			type: "spring" as const,
			damping: 15,
			stiffness: 80,
		},
	},
};

export const TimelineStat = ({ timeline }: { timeline: TimelineItem[] }) => {
	return (
		<div className="-mt-8 mx-auto max-w-7xl px-6 lg:px-8">
			<motion.div
				className="mx-auto grid max-w-2xl grid-cols-1 gap-8 overflow-hidden lg:mx-0 lg:max-w-none lg:grid-cols-4"
				variants={containerVariants}
				initial="hidden"
				whileInView="visible"
				viewport={{ once: true, amount: 0.25 }}
			>
				{timeline.map((item) => (
					<motion.div key={item.name} variants={itemVariants}>
						<motion.time
							dateTime={item.dateTime}
							className="flex items-center font-semibold text-base text-secondary leading-6"
							whileHover={{ scale: 1.02 }}
							transition={{ type: "spring", stiffness: 400, damping: 10 }}
						>
							<svg
								viewBox="0 0 4 4"
								className="mr-4 h-1 w-1 flex-none"
								aria-hidden="true"
							>
								<circle cx={2} cy={2} r={2} fill="currentColor" />
							</svg>
							{item.date}
							<div
								className="-ml-2 -translate-x-full sm:-ml-4 lg:-mr-6 absolute h-px w-screen bg-secondary lg:static lg:ml-8 lg:w-auto lg:flex-auto lg:translate-x-0"
								aria-hidden="true"
							/>
						</motion.time>
						<motion.div
							initial={{ opacity: 0 }}
							animate={{ opacity: 1 }}
							transition={{ delay: 0.3 }}
						>
							<Label className="mt-6 text-base">{item.name}</Label>
							<AdaptiveText
								portableText={item.description}
								className="mt-2 text-sm sm:text-base"
							/>
						</motion.div>
					</motion.div>
				))}
			</motion.div>
		</div>
	);
};
